import { ESymbolCategory, type IDisplayItem, type IDisplayItemProperty, type ISymbol } from "@kuika/kuika-cl-model";
import { TASK_FEED_FIELD_PREFIX, isTaskFeedAction } from "./task-feed-process-scope";
import { foldParamNameForMatch } from "./workflow-form-metadata-overrides";

/**
 * KOA-18136 — reading a task-inbox screen to find out what its feed must carry,
 * and which of its columns cannot honestly show anything.
 *
 * Two questions, both answered from the CANVAS rather than from anyone's memory of
 * what was rendered:
 *
 *   1. Which process fields does the screen actually display? A column bound to
 *      `data.<Param>` is the screen's own statement of what it needs, so the feed
 *      must be scoped to return exactly those — no wider (a lookup per task for a
 *      column nobody shows) and no narrower (a column that resolves to nothing).
 *
 *   2. Which columns are bound to `Status`? On this read `Status` is
 *      `EWorkflowStepStatus` as a raw `Int32`, so the column paints a digit; and
 *      every row in an inbox is a task that is WAITING, so it says nothing even
 *      when it renders. Three of four observed live builds bound it. The requester
 *      list has carried the "never bind a status column from this read" law since
 *      KOA-17362 §25 — the inbox never did.
 *
 * Pure walk over an explicit dictionary + display-item tree, so it is testable
 * without designer state.
 */

/** A process field the screen binds, and where. */
export interface ITaskFeedBoundField {
  /** Parameter name, without the `data.` prefix. */
  readonly name: string;
  readonly kuikaId: string;
  readonly propertyName: string;
}

/** A column that can only ever paint a raw enum — reported for removal. */
export interface ITaskFeedUnshowableColumn {
  /** The component holding the binding (a Label inside the column). */
  readonly boundKuikaId: string;
  /** The enclosing table column, when one was found — the thing worth removing. */
  readonly columnKuikaId?: string;
  readonly fieldName: string;
}

export interface ITaskFeedScreenReading {
  readonly boundFields: readonly ITaskFeedBoundField[];
  readonly unshowableColumns: readonly ITaskFeedUnshowableColumn[];
}

/** Component names that ARE a table column — the unit worth removing. */
const TABLE_COLUMN_COMPONENTS: ReadonlySet<string> = new Set<string>(["tabletemplatecolumn", "tabledefaultcolumn"]);

/**
 * Row fields whose value is a raw enum on the task read.
 *
 * Deliberately a LIST OF ONE rather than a heuristic over the return type: the
 * only such field on this read is `Status`, and inferring "looks like an enum"
 * from a type name would eventually remove a legitimate `Int32` column (a day
 * count, an amount, a running number).
 */
const RAW_ENUM_ROW_FIELDS: ReadonlySet<string> = new Set<string>(["status"]);

/**
 * The screen dictionary, keyed by kuikaId.
 *
 * Declared with a NUMBER index to match `IScreenDesignDictionary` (its real
 * declaration) while every lookup here goes through a string — TypeScript treats
 * the two as the same index in a numeric-keyed map, and accepting the platform's
 * own type is what lets the caller pass designer state without a cast.
 */
interface IDictionaryLike {
  [kuikaId: number]: IDisplayItem | undefined;
}

function actionFieldName(symbol: ISymbol | undefined): string | undefined {
  if (!symbol || symbol.category !== ESymbolCategory.actionField) return undefined;
  const fieldName: string = String(symbol.fieldName ?? "").trim();
  return fieldName.length > 0 ? fieldName : undefined;
}

/** Climb to the enclosing table column, if the binding sits inside one. */
function enclosingColumnKuikaId(dictionary: IDictionaryLike, kuikaId: string): string | undefined {
  const seen: Set<string> = new Set<string>();
  let current: IDisplayItem | undefined = dictionary[kuikaId as unknown as number];
  let currentId: string = kuikaId;
  // Bounded by `seen` — a corrupted parent chain must not spin here.
  while (current !== undefined && !seen.has(currentId)) {
    seen.add(currentId);
    const name: string = String(current.item?.name ?? "").toLowerCase();
    if (TABLE_COLUMN_COMPONENTS.has(name)) return currentId;
    const parentId: string | undefined =
      current.parentId === undefined || current.parentId === null ? undefined : String(current.parentId);
    if (parentId === undefined) return undefined;
    currentId = parentId;
    current = dictionary[parentId as unknown as number];
  }
  return undefined;
}

/**
 * What the screen says it needs, and what it cannot show.
 *
 * `taskFeedHosted` names the components whose enclosing repeater reads a task
 * feed. Only bindings inside those are considered: a `Status` column on an
 * ordinary project list is somebody else's field with somebody else's meaning, and
 * a `data.*` binding elsewhere belongs to whatever action feeds THAT list.
 */
export function readTaskFeedScreen(
  dictionary: IDictionaryLike,
  taskFeedHosted: ReadonlySet<string>
): ITaskFeedScreenReading {
  const boundFields: ITaskFeedBoundField[] = [];
  const unshowableColumns: ITaskFeedUnshowableColumn[] = [];
  const seenField: Set<string> = new Set<string>();

  for (const kuikaId of Object.keys(dictionary)) {
    if (!taskFeedHosted.has(kuikaId)) continue;
    const properties: readonly IDisplayItemProperty[] =
      dictionary[kuikaId as unknown as number]?.item?.properties ?? [];
    for (const property of properties) {
      const fieldName: string | undefined = actionFieldName(property.symbol);
      if (fieldName === undefined) continue;

      const lower: string = fieldName.toLowerCase();
      if (lower.startsWith(`${TASK_FEED_FIELD_PREFIX}.`)) {
        const name: string = fieldName.slice(TASK_FEED_FIELD_PREFIX.length + 1).trim();
        const folded: string = foldParamNameForMatch(name);
        if (name.length === 0 || seenField.has(folded)) continue;
        seenField.add(folded);
        boundFields.push({ name, kuikaId, propertyName: property.name });
        continue;
      }

      if (RAW_ENUM_ROW_FIELDS.has(foldParamNameForMatch(fieldName))) {
        const columnKuikaId: string | undefined = enclosingColumnKuikaId(dictionary, kuikaId);
        unshowableColumns.push({
          boundKuikaId: kuikaId,
          ...(columnKuikaId !== undefined ? { columnKuikaId } : {}),
          fieldName
        });
      }
    }
  }

  return { boundFields, unshowableColumns };
}

/**
 * The components sitting inside a repeater whose datasource is a task feed.
 *
 * Resolution is by the repeater's datasource ACTION NAME, taken from
 * `resolveDatasourceActionName` — the caller supplies it because reading a
 * `dataSource` symbol back to an action name needs the screen's action list, which
 * is designer state rather than tree shape.
 */
export function collectTaskFeedHostedComponents(
  dictionary: IDictionaryLike,
  resolveDatasourceActionName: (di: IDisplayItem) => string | undefined
): Set<string> {
  const hosted: Set<string> = new Set<string>();
  const feedHosts: string[] = [];

  for (const kuikaId of Object.keys(dictionary)) {
    const di: IDisplayItem | undefined = dictionary[kuikaId as unknown as number];
    if (di === undefined) continue;
    const actionName: string | undefined = resolveDatasourceActionName(di);
    if (actionName !== undefined && isTaskFeedAction(actionName)) feedHosts.push(kuikaId);
  }
  if (feedHosts.length === 0) return hosted;

  // Everything BELOW a feed host, host included: the cells live in its subtree.
  for (const kuikaId of Object.keys(dictionary)) {
    const seen: Set<string> = new Set<string>();
    let currentId: string = kuikaId;
    let current: IDisplayItem | undefined = dictionary[currentId as unknown as number];
    while (current !== undefined && !seen.has(currentId)) {
      seen.add(currentId);
      if (feedHosts.includes(currentId)) {
        hosted.add(kuikaId);
        break;
      }
      const parentId: string | undefined =
        current.parentId === undefined || current.parentId === null ? undefined : String(current.parentId);
      if (parentId === undefined) break;
      currentId = parentId;
      current = dictionary[parentId as unknown as number];
    }
  }
  return hosted;
}
